import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import styled from '@emotion/styled'
import { Button, Col, Row, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/lib/table'
import { useSession } from 'packages/@slerp/accounts'
import { GET_LOYALTY_MULTIPLIERS } from './queries'
import {
  GetLoyaltyMultipliersData,
  GetLoyaltyMultipliersVars,
  LoyaltyMultiplierListItem
} from './types'
import {
  STATUS_DISPLAY,
  appliesToLabel,
  boostLabel,
  sortMultipliers,
  whenLabel,
  whenTooltip
} from './listUtils'

const { Title, Paragraph } = Typography

const CREATE_PATH = '/loyalty/multipliers/create'

const EMPTY_BODY =
  'Multipliers are a great way of leveraging your existing loyalty programme to drive sales where they matter most to you. Promote new menu items or increase orders on slow days with a new Loyalty Multiplier'

const ERROR_BODY = "Couldn't load multipliers. Refresh the page to try again."

const columns: ColumnsType<LoyaltyMultiplierListItem> = [
  { title: 'Name', dataIndex: 'name', key: 'name' },
  {
    title: 'Applies to',
    key: 'appliesTo',
    render: (_, item) => appliesToLabel(item)
  },
  {
    title: 'Loyalty card',
    key: 'loyaltyCard',
    render: (_, item) => item.loyaltyCard?.name ?? '—'
  },
  {
    title: 'When',
    key: 'when',
    render: (_, { days }) => {
      const tooltip = whenTooltip(days)
      const label = whenLabel(days)
      return tooltip ? <Tooltip title={tooltip}>{label}</Tooltip> : label
    }
  },
  {
    title: 'Boost',
    key: 'boost',
    render: (_, { factor }) => boostLabel(factor)
  },
  {
    title: 'Status',
    key: 'status',
    render: (_, { status }) => {
      const { label, color } = STATUS_DISPLAY[status]
      return (
        <Tag style={{ color, borderColor: color, background: 'transparent' }}>
          {label}
        </Tag>
      )
    }
  }
]

interface EmptyStateProps {
  heading?: string
  body: string
}

const EmptyState = ({ heading, body }: EmptyStateProps) => (
  <EmptyCard>
    {heading && (
      <Title level={4} className='_mb-0'>
        {heading}
      </Title>
    )}
    <EmptyBody>{body}</EmptyBody>
  </EmptyCard>
)

export const MultipliersList = () => {
  const navigate = useNavigate()
  const { merchant } = useSession()

  const { data, loading, error } = useQuery<
    GetLoyaltyMultipliersData,
    GetLoyaltyMultipliersVars
  >(GET_LOYALTY_MULTIPLIERS, {
    fetchPolicy: 'no-cache',
    variables: { merchantId: merchant?.id || '' }
  })

  const multipliers = useMemo(
    () => sortMultipliers(data?.loyaltyMultipliers ?? []),
    [data]
  )

  const renderBody = () => {
    if (error) return <EmptyState body={ERROR_BODY} />
    if (!loading && multipliers.length === 0) {
      return <EmptyState heading='No Multipliers Yet' body={EMPTY_BODY} />
    }
    return (
      <Table
        dataSource={multipliers}
        columns={columns}
        rowKey='id'
        loading={loading}
        onRow={({ id }) => ({
          className: '_pointer',
          onClick: () => navigate(`/loyalty/multipliers/${id}`)
        })}
        pagination={{
          defaultPageSize: 100,
          position: ['bottomCenter'],
          showSizeChanger: true
        }}
      />
    )
  }

  return (
    <div data-testid='multipliers-list'>
      <Row justify='space-between' align='middle' className='_mt-16 _mb-32'>
        <Col>
          <Title level={3} className='_mb-0'>
            Multipliers
          </Title>
        </Col>
        <Col>
          <Button type='primary' onClick={() => navigate(CREATE_PATH)}>
            SETUP MULTIPLIERS
          </Button>
        </Col>
      </Row>
      {renderBody()}
    </div>
  )
}

const EmptyCard = styled.div`
  margin-bottom: 32px;
  padding: 40px 24px;
  text-align: center;
  border: 1px solid #e7e5e9;
  border-radius: 6px;
`

const EmptyBody = styled(Paragraph)`
  max-width: 720px;
  margin: 16px auto 0;
`
